import { useEffect, type RefObject } from 'react';

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

/**
 * Position the security-search dropdown under its input and dismiss it on an
 * outside click. Extracted verbatim from useCreateTradeForm (LR-495); the two
 * effects keep their original dependency arrays so behaviour is unchanged.
 */
export function useSecuritySearchDropdownEffects({
  showSecurityDropdown,
  securityInputRef,
  securityDropdownRef,
  setDropdownPosition,
  setShowSecurityDropdown,
}: {
  showSecurityDropdown: boolean;
  securityInputRef: RefObject<HTMLInputElement | null>;
  securityDropdownRef: RefObject<HTMLDivElement | null>;
  setDropdownPosition: (p: DropdownPosition) => void;
  setShowSecurityDropdown: (v: boolean) => void;
}) {
  useEffect(() => {
    if (showSecurityDropdown && securityInputRef.current) {
      const rect = securityInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    // ref + stable setter omitted to keep the original [showSecurityDropdown] trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSecurityDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        securityDropdownRef.current &&
        !securityDropdownRef.current.contains(event.target as Node) &&
        securityInputRef.current &&
        !securityInputRef.current.contains(event.target as Node)
      ) {
        setShowSecurityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // refs + stable setter; original effect registered once on mount (empty deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
